import { useEffect, useState } from 'react';
import { Bell, BellRing, CheckCheck } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from 'helpers/cn';
import { useNotifications, useUnreadCount } from '@/state/notifications/useNotifications';
import { NotificationRow } from './NotificationRow';

export const NotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const unreadCount = useUnreadCount();
  const { notifications, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage, markSeen, markRead } =
    useNotifications(isOpen);

  // Opening the panel clears the badge but not the per-row unread state, so
  // rows stay highlighted until they are actually clicked.
  useEffect(() => {
    if (isOpen && unreadCount > 0) markSeen();
  }, [isOpen]);

  const hasUnread = unreadCount > 0;

  return (
    <>
      {/* Same shape as the header's theme toggle, so the two controls read as a pair.
          The field fill is what draws the button, which is why the hairline is enough
          here. The badge carries the emphasis: two loud controls side by side are
          clutter. */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="border-line bg-field text-dim hover:text-foreground rounded-pill relative flex size-[30px] items-center justify-center border transition-colors duration-150"
        aria-label={hasUnread ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        title="Notifications"
        data-tour="header-notifications"
      >
        {hasUnread ? <BellRing className="size-4" strokeWidth={1.5} /> : <Bell className="size-4" strokeWidth={1.5} />}
        {hasUnread && (
          <span
            className={cn(
              'bg-accent text-accent-foreground rounded-pill absolute -right-1 -top-1',
              'flex h-4 min-w-4 items-center justify-center px-1',
              'font-mono text-nano font-medium leading-none',
            )}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        {/* A panel on the desk, not a second background: the sheet's default is
            `bg-background`, which is the desk itself and would leave the rows floating
            on nothing. */}
        <SheetContent side="right" className="bg-panel border-line flex w-full flex-col gap-0 p-0 sm:max-w-md">
          {/* pr-12 leaves the sheet's own close button its corner, so "mark all read"
              cannot end up underneath it. */}
          <SheetHeader className="bg-panel2 border-line flex-row items-center justify-between space-y-0 border-b px-4 py-3 pr-12">
            <SheetTitle className="text-foreground text-body font-medium">Notifications</SheetTitle>
            {notifications.some((notification) => !notification.readAt) && (
              <button
                onClick={() => markRead({ all: true })}
                className="micro-label text-dim hover:text-foreground flex items-center gap-1.5 transition-colors duration-150"
              >
                <CheckCheck className="size-3.5" />
                Mark all read
              </button>
            )}
          </SheetHeader>

          <div className="flex-1 overflow-y-auto">
            {isLoading && (
              <div className="flex flex-col gap-3 p-4">
                <Skeleton className="bg-panel2 rounded-card h-14 w-full" />
                <Skeleton className="bg-panel2 rounded-card h-14 w-full" />
                <Skeleton className="bg-panel2 rounded-card h-14 w-full" />
              </div>
            )}

            {!isLoading && !notifications.length && (
              <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
                <Bell className="text-dim size-7" strokeWidth={1.5} />
                <p className="micro-label text-foreground">Nothing yet</p>
                <p className="supporting max-w-xs">Experiment activity and results will show up here as they happen.</p>
              </div>
            )}

            {notifications.map((notification) => (
              <NotificationRow
                key={notification.id}
                notification={notification}
                onRead={(id) => markRead({ ids: [id] })}
                onNavigated={() => setIsOpen(false)}
              />
            ))}

            {hasNextPage && (
              <div className="p-4">
                <button
                  type="button"
                  disabled={isFetchingNextPage}
                  onClick={() => fetchNextPage()}
                  className="border-line text-dim hover:text-foreground hover:bg-hover micro-label flex h-8 w-full items-center justify-center rounded-lg border transition-colors duration-150 disabled:opacity-40"
                >
                  {isFetchingNextPage ? 'Loading' : 'Load more'}
                </button>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
